import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Send, CheckCircle, Clock, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const { API, accessToken } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [recentEnvelopes, setRecentEnvelopes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };

      // Fetch analytics
      const analyticsRes = await axios.get(`${API}/analytics/throughput`, { headers });
      setStats(analyticsRes.data);

      // Fetch recent envelopes
      const envelopesRes = await axios.get(`${API}/envelopes?limit=5`, { headers });
      setRecentEnvelopes(envelopesRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-2">Overview of your e-signature activity</p>
          </div>
          <Link to="/envelopes">
            <Button
              className="bg-gradient-to-r from-indigo-600 to-purple-600"
              data-testid="create-envelope-btn"
            >
              <Send size={18} className="mr-2" />
              New Envelope
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6">
          <Card className="glass-effect border-none card-shadow" data-testid="stat-total-envelopes">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Envelopes</CardTitle>
              <FileText className="text-indigo-600" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_envelopes || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-none card-shadow" data-testid="stat-completed">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Completed</CardTitle>
              <CheckCircle className="text-green-600" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.completed || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Successfully signed</p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-none card-shadow" data-testid="stat-in-progress">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">In Progress</CardTitle>
              <Clock className="text-orange-600" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.in_progress || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Awaiting signatures</p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-none card-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Completion Rate</CardTitle>
              <TrendingUp className="text-purple-600" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {stats?.total_envelopes > 0
                  ? Math.round((stats.completed / stats.total_envelopes) * 100)
                  : 0}%
              </div>
              <p className="text-xs text-gray-500 mt-1">Success metric</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Envelopes */}
        <Card className="glass-effect border-none card-shadow">
          <CardHeader>
            <CardTitle>Recent Envelopes</CardTitle>
          </CardHeader>
          <CardContent>
            {recentEnvelopes.length > 0 ? (
              <div className="space-y-4">
                {recentEnvelopes.map((envelope) => (
                  <Link
                    key={envelope.id}
                    to={`/envelopes/${envelope.id}`}
                    className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid={`envelope-${envelope.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        envelope.status === 'completed'
                          ? 'bg-green-100 text-green-600'
                          : envelope.status === 'sent'
                          ? 'bg-orange-100 text-orange-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <FileText size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{envelope.title}</h3>
                        <p className="text-sm text-gray-500">
                          {new Date(envelope.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        envelope.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : envelope.status === 'sent'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {envelope.status}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-500 mb-4">No envelopes yet</p>
                <Link to="/envelopes">
                  <Button variant="outline">Create Your First Envelope</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;